import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { curatorCardApi } from '@/api/curatorCard';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { SearchInput } from '@/components/ui/SearchInput';
import { homeworkStatusKey } from '@/lib/curatorCardActions';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import type { CuratorReviewItem } from '@/api/curatorCard';

/**
 * Лента проверок куратора, новые сверху: оценки уроков и решения по домашкам (договор `curator-card.md` §3).
 *
 * ⚠️ У ДОМАШКИ ВИДЕН ТОЛЬКО ПОСЛЕДНИЙ КРУГ. Истории кругов у домашки нет: пересдача перезаписывает строку. Прежнее
 * решение куратора по той же работе в ленте не появится — это свойство данных, а не экрана.
 */
export function CuratorReviewsFeed({ curatorId }: { curatorId: string }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Поиск по ученику и уроку — на сервере, по всей ленте, а не по загруженным страницам (28.09.2026, вторая волна п. 8).
  const [q, setQ] = useState('');
  const feed = useInfiniteQuery({
    queryKey: ['admin', 'curator', curatorId, 'reviews', q],
    queryFn: ({ pageParam }) => curatorCardApi.reviews(curatorId, pageParam, q),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before,
  });
  const items = feed.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Card className="mb-4">
      <CardTitle>{t('adminCuratorCard.feedTitle')}</CardTitle>
      <SearchInput className="mt-3" value={q} placeholder={t('adminCuratorCard.feedSearch')} onSearch={setQ} />
      {feed.isLoading ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : feed.error ? (
        <p className="mt-3 text-sm text-error">{t('adminCuratorCard.loadError')}</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-on-surface-variant">
          {q ? t('common.nothingFound') : t('adminCuratorCard.feedEmpty')}
        </p>
      ) : isMobile ? (
        // Телефон (§5.7 п. 5): таблица в 760 px уезжала вбок — строками: «когда · ученик», урок и курс, итог меткой,
        // отзыв до четырёх строк. Без внутренней прокрутки, «Показать ещё» внизу.
        <div className="mt-2" data-curator-feed-phone>
          {items.map((it, i) => (
            <div
              key={`${it.kind}-${it.student_id}-${it.at}-${i}`}
              className={cn('space-y-1 py-3', i < items.length - 1 && 'border-b border-outline')}
            >
              <p className="text-[11.5px] text-on-surface-variant">
                {new Date(it.at).toLocaleString('ru-RU')} ·{' '}
                <Link to={`/admin/students/${it.student_id}`} className="font-semibold text-on-surface">
                  {it.student_name || it.student_id.slice(0, 8)}
                </Link>
              </p>
              <p className="text-[13.5px] font-semibold leading-snug text-on-surface">{it.lesson_title ?? '—'}</p>
              {it.marathon_title && <p className="text-[11.5px] text-on-surface-variant">{it.marathon_title}</p>}
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                <ReviewResult it={it} />
              </div>
              {it.feedback && (
                <p className="line-clamp-4 whitespace-pre-line text-[12.5px] text-on-surface-variant">{it.feedback}</p>
              )}
            </div>
          ))}
          {feed.hasNextPage && (
            <Button
              variant="secondary"
              onClick={() => feed.fetchNextPage()}
              loading={feed.isFetchingNextPage}
              className="mt-2 w-full"
            >
              {t('adminCuratorCard.showMore')}
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-3 max-h-[40rem] overflow-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-xs uppercase tracking-label text-on-surface-variant">
              <tr>
                <th className="py-2 pr-3">{t('adminCuratorCard.colDate')}</th>
                <th className="py-2 pr-3">{t('adminCuratorCard.colStudent')}</th>
                <th className="py-2 pr-3">{t('adminCuratorCard.colLesson')}</th>
                <th className="py-2 pr-3">{t('adminCuratorCard.colResult')}</th>
                <th className="py-2 pr-3">{t('adminCuratorCard.colFeedback')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={`${it.kind}-${it.student_id}-${it.at}-${i}`} className="border-t border-outline-variant/40 align-top">
                  <td className="whitespace-nowrap py-2 pr-3 text-on-surface-variant">
                    {new Date(it.at).toLocaleString('ru-RU')}
                  </td>
                  <td className="py-2 pr-3">
                    <Link to={`/admin/students/${it.student_id}`} className="hover:underline">
                      {it.student_name || it.student_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="py-2 pr-3">
                    <p>{it.lesson_title ?? '—'}</p>
                    {it.marathon_title && <p className="text-xs text-on-surface-variant">{it.marathon_title}</p>}
                  </td>
                  <td className="py-2 pr-3">
                    <ReviewResult it={it} />
                  </td>
                  <td className="max-w-[320px] whitespace-pre-line py-2 pr-3 text-on-surface-variant">
                    {it.feedback || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {feed.hasNextPage && (
            <div className="mt-3">
              <Button variant="secondary" size="sm" onClick={() => feed.fetchNextPage()} loading={feed.isFetchingNextPage}>
                {t('adminCuratorCard.showMore')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Итог проверки меткой: решение по домашке или оценка урока; одна разметка на таблицу и на строки телефона. */
function ReviewResult({ it }: { it: CuratorReviewItem }) {
  const { t } = useTranslation();
  return (
    <>
      {it.kind === 'homework' ? (
        <Pill variant={it.status === 'approved' ? 'success' : 'warning'}>
          {t('adminCuratorCard.kindHomework')}: {t(homeworkStatusKey(it.status))}
        </Pill>
      ) : it.grade == null ? (
        <Pill>{t('adminCuratorCard.gradeCleared')}</Pill>
      ) : (
        <Pill variant="primary">{t('adminCuratorCard.grade', { n: it.grade })}</Pill>
      )}
      {it.credited_without_answers && (
        <div className="mt-1">
          <Pill variant="danger">{t('adminCuratorCard.creditedWithoutAnswers')}</Pill>
        </div>
      )}
    </>
  );
}
